import { centralDayKey, previousDayKey } from 'components/Streaks/model/centralTime';
import { STATE_FIXTURES } from 'components/Streaks/model/fixtures';
import { BuyFreezesRequest, StreakSnapshot } from 'components/Streaks/model/types';

/**
 * Dev-only stand-in for the streak backend, so the surfaces can be built and
 * reviewed before player-api has the routes. Lower environments only (same
 * gate as LobbyLab), and only when an operator sets
 * `localStorage.hijack_streaks_mock` to a state name from STATE_FIXTURES
 * ("In progress", "Milestone day", ...). Production builds never read it.
 */
const stage = (process.env.REACT_APP_ENVIRONMENT ?? '').toLowerCase();
const MOCK_AVAILABLE =
  process.env.NODE_ENV !== 'production' || /^(develop|test|staging)/.test(stage);
const KEY = 'hijack_streaks_mock';

let mockState: StreakSnapshot | null = null;

export function streakMockEnabled(): boolean {
  if (!MOCK_AVAILABLE) return false;
  try {
    return !!localStorage.getItem(KEY);
  } catch {
    return false;
  }
}

/** Shift a fixture onto today's real gaming day so the clock-driven states line up. */
function rebase(snap: StreakSnapshot): StreakSnapshot {
  const today = centralDayKey(Date.now());
  const fixtureToday = snap.day;
  const map = (d: string | null | undefined) => {
    if (!d) return d ?? null;
    if (d === fixtureToday) return today;
    if (d === previousDayKey(fixtureToday)) return previousDayKey(today);
    return previousDayKey(previousDayKey(today));
  };
  return {
    ...snap,
    day: today,
    asOf: Date.now(),
    lastQualifiedDay: map(snap.lastQualifiedDay) as string | null,
    lastFreezeUsedDay: map(snap.lastFreezeUsedDay) as string | null,
    notice: snap.notice && {
      freezeUsedFor: snap.notice.freezeUsedFor ? previousDayKey(today) : undefined,
      ended: snap.notice.ended && { ...snap.notice.ended, day: previousDayKey(today) },
      milestoneReached: snap.notice.milestoneReached && {
        ...snap.notice.milestoneReached,
        day: today
      }
    }
  };
}

function current(): StreakSnapshot {
  if (!mockState) {
    let name = 'In progress';
    try {
      name = localStorage.getItem(KEY) || name;
    } catch {
      /* default */
    }
    mockState = rebase(STATE_FIXTURES[name] || STATE_FIXTURES['In progress']);
  }
  return mockState;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function mockStreakRequest(path: string, init: RequestInit): Promise<StreakSnapshot> {
  await delay(250);
  const snap = current();
  if (path === '/streaks/freezes' && init.method === 'POST') {
    const body = JSON.parse(String(init.body)) as BuyFreezesRequest;
    mockState = {
      ...snap,
      version: snap.version + 1,
      asOf: Date.now(),
      freezeBalance: snap.freezeBalance + body.quantity,
      promoFreezes: snap.promoFreezes + (body.source === 'promo' ? body.quantity : 0),
      cashFreezes: snap.cashFreezes + (body.source === 'cash' ? body.quantity : 0)
    };
    return mockState;
  }
  if (path === '/streaks/frame' && init.method === 'PUT') {
    const { showFrame } = JSON.parse(String(init.body));
    mockState = { ...snap, version: snap.version + 1, asOf: Date.now(), showFrame: !!showFrame };
    return mockState;
  }
  return snap;
}
